import api from './api';

class LeaveBalanceService {
  /**
   * Get HR employees for leave balance assignment
   * @returns {Promise<Object>} API response
   */
  static async getHREmployees() {
    try {
      const response = await api.get('/leave-balance/hr-employees');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Assign leave balance to employee
   * @param {Object} balanceData - Leave balance data
   * @returns {Promise<Object>} API response
   */
  static async assignLeaveBalance(balanceData) {
    try {
      const response = await api.post('/leave-balance/assign', balanceData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get user's leave balances
   * @param {string} userId - User ID
   * @param {number} year - Year (optional)
   * @returns {Promise<Object>} API response
   */
  static async getUserLeaveBalances(userId, year) {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);

      const response = await api.get(`/leave-balance/user/${userId}?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get my leave balances
   * @param {number} year - Year (optional)
   * @returns {Promise<Object>} API response
   */
  static async getMyLeaveBalances(year) {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);

      const response = await api.get(`/leave-balance/my-balances?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get all leave balances
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} API response
   */
  static async getAllLeaveBalances(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.year) params.append('year', filters.year);
      if (filters.leaveTypeId) params.append('leaveTypeId', filters.leaveTypeId);
      if (filters.userId) params.append('userId', filters.userId);

      const response = await api.get(`/leave-balance/all?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update leave balance
   * @param {string} balanceId - Leave balance ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} API response
   */
  static async updateLeaveBalance(balanceId, updateData) {
    try {
      const response = await api.patch(`/leave-balance/update/${balanceId}`, updateData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete leave balance
   * @param {string} balanceId - Leave balance ID
   * @returns {Promise<Object>} API response
   */
  static async deleteLeaveBalance(balanceId) {
    try {
      const response = await api.delete(`/leave-balance/delete/${balanceId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Reset leave balances for new year
   * @param {number} year - New year
   * @returns {Promise<Object>} API response
   */
  static async resetLeaveBalancesForNewYear(year) {
    try {
      const response = await api.post('/leave-balance/reset-year', { year });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Bulk assign leave balances
   * @param {Array} assignments - Array of assignment objects
   * @returns {Promise<Object>} API response
   */
  static async bulkAssignLeaveBalances(assignments) {
    try {
      const response = await api.post('/leave-balance/bulk-assign', { assignments });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
}

export default LeaveBalanceService;